/**
 * Hook to fetch OAuth settings from system_settings
 *
 * Returns the enabled state for Google and Apple OAuth,
 * combining both system settings from Supabase and platform checks.
 */

import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  isGoogleOAuthEnabled,
  isAppleOAuthEnabled,
  prefetchSettings,
} from '@/services/systemSettings';

// Also check environment variables as fallback
const ENV_GOOGLE_ENABLED = import.meta.env.VITE_ENABLE_GOOGLE_AUTH === 'true';
const ENV_APPLE_ENABLED = import.meta.env.VITE_ENABLE_APPLE_AUTH === 'true';

// Platform detection (computed once at module load)
const PLATFORM_IOS = Capacitor.getPlatform() === 'ios';
const PLATFORM_ANDROID = Capacitor.getPlatform() === 'android';

interface OAuthSettings {
  googleEnabled: boolean;
  appleEnabled: boolean;
  isLoading: boolean;
}

/**
 * Hook to get OAuth availability status
 * Fetches settings from Supabase and combines with platform checks
 *
 * Platform rules:
 * - iOS: Only Apple Sign-In (no Google)
 * - Android: Only Google Sign-In (no Apple)
 * - Web/Browser: Both providers available
 */
export function useOAuthSettings(): OAuthSettings {
  const [settings, setSettings] = useState<OAuthSettings>({
    // Apply platform rules to initial state as well
    googleEnabled: ENV_GOOGLE_ENABLED && !PLATFORM_IOS,
    appleEnabled: ENV_APPLE_ENABLED && !PLATFORM_ANDROID,
    isLoading: true,
  });

  useEffect(() => {
    let mounted = true;

    async function loadSettings() {
      try {
        const [googleEnabled, appleEnabled] = await Promise.all([
          isGoogleOAuthEnabled(),
          isAppleOAuthEnabled(),
        ]);

        if (!mounted) return;

        // Apply platform rules
        const googleAvailable = (googleEnabled || ENV_GOOGLE_ENABLED) && !PLATFORM_IOS;
        const appleAvailable = (appleEnabled || ENV_APPLE_ENABLED) && !PLATFORM_ANDROID;

        setSettings({
          googleEnabled: googleAvailable,
          appleEnabled: appleAvailable,
          isLoading: false,
        });
      } catch (error) {
        console.error('Error loading OAuth settings:', error);
        if (mounted) {
          // Fall back to env variables on error, still respecting platform rules
          setSettings({
            googleEnabled: ENV_GOOGLE_ENABLED && !PLATFORM_IOS,
            appleEnabled: ENV_APPLE_ENABLED && !PLATFORM_ANDROID,
            isLoading: false,
          });
        }
      }
    }

    loadSettings();

    return () => {
      mounted = false;
    };
  }, []);

  return settings;
}

// Pre-fetch settings when module loads
prefetchSettings();
